// formatFileSize/formatContentType are shared with the external upload page; re-exported here so
// the existing file-helper consumers keep importing them from one place.
export { formatContentType, formatFileSize } from "@shared";

/**
 * Splits a file name into its base name and extension (including the leading dot).
 * A leading dot (dotfiles) or a missing dot yields an empty extension and the full name as base.
 */
export function splitFileName(fileName: string): { baseName: string; extension: string } {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot <= 0) {
    return { baseName: fileName, extension: "" };
  }
  return { baseName: fileName.substring(0, lastDot), extension: fileName.substring(lastDot) };
}

export function downloadFile(blob: Blob, fileName: string) {
  const url: string = window.URL.createObjectURL(blob);
  downloadFileFromURL(url, fileName);
}

export function downloadFileFromURL(url: string, fileName: string) {
  const a = document.createElement("a");
  a.style.display = "none";
  document.body.appendChild(a);

  a.href = url;
  a.download = fileName;
  a.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export function openFileInNewTab(blob: Blob) {
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_blank");
  URL.revokeObjectURL(blobUrl); // Clean up URL after opening
}

export function getFileNameFromResponse(response: Response, defaultFileName: string | undefined = "") {
  const contentDisposition = response.headers.get("content-disposition");

  // With both forms present, RFC 6266 says to prefer `filename*`: plain `filename` cannot carry
  // non-ASCII, so ASP.NET Core fills it with a degraded copy ("ü" becomes "_") and puts the real name
  // in `filename*` as percent-encoded UTF-8.
  // Matches: filename*=UTF-8''my%C3%9Cmlaut.txt
  const extendedName = contentDisposition?.match(/filename\*=UTF-8''([^";]+)/i)?.[1];
  if (extendedName) {
    return decodeURIComponent(extendedName);
  }

  // Matches: filename="myFile.txt" OR filename=myFile.txt
  const match = contentDisposition?.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? defaultFileName;
}
